// The GPU side: one WebGL canvas, one scene, and a frame loop that only runs while something moves.

import { Color, OrthographicCamera, Scene, Vector2, Vector3, Vector4, WebGLRenderer } from "three";
import type { IUniform } from "three";
import { ROLL } from "../view/camera.ts";
import type { Camera } from "../view/camera.ts";
import { DAY, NIGHT } from "../theme.ts";
import type { Rgb } from "../theme.ts";
import { MAX_COLUMNS } from "./glsl.ts";

const v3 = (c: Rgb) => new Vector3(c[0], c[1], c[2]);

/** Uniforms every layer shares: the camera's mapping and the edition's colours. Updated once per frame. */
export interface SharedUniforms {
  [name: string]: IUniform;
  uEdges: IUniform<Float32Array>;
  uSplits: IUniform<Float32Array>;
  uColumns: IUniform<number>;
  uPlot: IUniform<Vector4>;
  uWindow: IUniform<Vector2>;
  uViewport: IUniform<Vector2>;
  uPixelRatio: IUniform<number>;
  uNight: IUniform<number>;
  uPigDay: IUniform<Vector3[]>;
  uPigNight: IUniform<Vector3[]>;
  uPaper: IUniform<Vector3[]>;
  uGap: IUniform<Vector3[]>;
  uPool: IUniform<Vector3[]>;
  uInk: IUniform<Vector3[]>;
  /** px per century across the screen, as log2 of its ratio to 40 px: keeps the pen's wobble one size. */
  uTimeZoom: IUniform<number>;
  uLight: IUniform<Vector2>;
  uLightOn: IUniform<number>;
}

export class Stage {
  readonly renderer: WebGLRenderer;
  readonly scene = new Scene();
  readonly uniforms: SharedUniforms;
  private readonly view = new OrthographicCamera(0, 1, 0, 1, -1, 1);
  private frameRequested = false;
  private last = 0;
  /** Called before every frame is drawn; returns whether anything is still moving. */
  onFrame: (dt: number) => boolean = () => false;
  /** Frame durations (ms) of recent frames, for measuring. */
  readonly frameTimes: number[] = [];

  constructor(canvas: HTMLCanvasElement) {
    this.renderer = new WebGLRenderer({ canvas, antialias: true, powerPreference: "high-performance" });
    this.renderer.setPixelRatio(Math.min(2, window.devicePixelRatio));
    this.renderer.autoClear = true;
    this.renderer.setClearColor(new Color(DAY.paper[0], DAY.paper[1], DAY.paper[2]));
    this.uniforms = {
      uEdges: { value: new Float32Array(MAX_COLUMNS + 1) },
      uSplits: { value: new Float32Array(MAX_COLUMNS).fill(0.5) },
      uColumns: { value: 1 },
      uPlot: { value: new Vector4() },
      uWindow: { value: new Vector2(0, 1) },
      uViewport: { value: new Vector2(1, 1) },
      uPixelRatio: { value: 1 },
      uNight: { value: 0 },
      uPigDay: { value: DAY.pigments.map(v3) },
      uPigNight: { value: NIGHT.pigments.map(v3) },
      uPaper: { value: [v3(DAY.paper), v3(NIGHT.paper)] },
      uGap: { value: [v3(DAY.gap), v3(NIGHT.gap)] },
      uPool: { value: [v3(DAY.pool), v3(NIGHT.pool)] },
      uInk: { value: [v3(DAY.ink), v3(NIGHT.ink)] },
      uTimeZoom: { value: 0 },
      uLight: { value: new Vector2(-1e4, -1e4) },
      uLightOn: { value: 0 },
    };
    document.addEventListener("visibilitychange", this.wake);
    window.addEventListener("focus", this.wake);
  }

  resize(width: number, height: number): void {
    this.renderer.setSize(width, height, false);
    this.uniforms.uViewport.value.set(width, height);
    this.uniforms.uPixelRatio.value = this.renderer.getPixelRatio();
    this.invalidate();
  }

  /** Asks for a frame. Nothing is drawn while nothing changes. */
  /**
   * A hidden page gets no animation frames, and a frame asked for while hidden may never arrive. When the page
   * shows again, ask afresh, so a half-finished movement completes instead of waiting for the next touch.
   */
  private readonly wake = () => {
    if (document.visibilityState !== "visible") return;
    this.frameRequested = false;
    this.last = 0;
    this.invalidate();
  };

  invalidate(): void {
    if (this.frameRequested) return;
    this.frameRequested = true;
    requestAnimationFrame((t) => this.frame(t));
  }

  private frame(now: number): void {
    this.frameRequested = false;
    const dt = this.last ? Math.min(0.05, (now - this.last) / 1000) : 1 / 60;
    this.last = now;
    const t0 = performance.now();
    const moving = this.onFrame(dt);
    this.renderer.render(this.scene, this.view);
    this.frameTimes.push(performance.now() - t0);
    if (this.frameTimes.length > 600) this.frameTimes.splice(0, 300);
    if (moving) this.invalidate();
    else this.last = 0;
  }

  /** Copies the camera into the shared uniforms. */
  syncCamera(camera: Camera): void {
    const u = this.uniforms;
    u.uColumns.value = camera.edges.length - 1;
    u.uEdges.value.set(camera.edges);
    u.uSplits.value.set(camera.splitValues);
    const p = camera.plot;
    u.uPlot.value.set(p.left, p.top, p.width, p.height);
    const { u0, u1 } = camera.window;
    u.uWindow.value.set(u0, u1);
    // A century is 1/50 of u; the wobble is drawn for about 40 px a century and keeps that size at any zoom.
    const pxPerTick = (p.width * (1 - 2 * ROLL)) / (u1 - u0) / 50;
    u.uTimeZoom.value = Math.log2(Math.max(1e-3, pxPerTick / 40));
  }
}
